/**
 * 출력 서식 카탈로그 시드 (dataset · report · 디폴트 서식 · 미리보기 샘플)
 * SSOT: D:\295\erp\CM\design\06_출력서식_카탈로그.md
 */

export const REPORT_SCOPE = {
  DEFAULT: 'DEFAULT',
  TENANT: 'TENANT',
};

export const DATASET_ID = {
  BUDGET_REQ_LINE: 'DS_BUDGET_REQ_LINE',
};

export const REPORT_ID = {
  BUDGET_REQ_LINE: 'RPT_BUDGET_REQ_LINE',
};

/** 시드 디폴트 PK — 운영 복사본은 tpl- UUID */
export const TPL_DEFAULT_BUDGET_REQ_LINE = 'tpl-default-RPT_BUDGET_REQ_LINE';

const F = (field, label, extra = {}) => ({
  field,
  label,
  type: extra.type || 'text',
  align: extra.align || (extra.type === 'number' ? 'right' : 'left'),
  sumYn: extra.sumYn === true,
  printDefault: extra.printDefault !== false,
  mergeYn: extra.mergeYn === true,
  widthMm: extra.widthMm || 22,
});

/** 개발이 등록한 데이터 계약. 화면 gridId에 묶지 않음. */
export const REPORT_DATASETS = [
  {
    datasetId: DATASET_ID.BUDGET_REQ_LINE,
    label: '편성내역 라인',
    mergeGroupKey: 'requestUid',
    splitKeys: ['deptCode', 'projectCode', 'subjectCode', 'fundCode', 'acctUnitCode', 'requestUid'],
    fields: [
      F('acctUnitCode', '회계단위코드', { printDefault: false, mergeYn: true, widthMm: 22 }),
      F('acctUnitNm', '회계단위명', { printDefault: false, mergeYn: true, widthMm: 28 }),
      F('deptCode', '부서코드', { mergeYn: true, widthMm: 20 }),
      F('deptName', '부서명', { mergeYn: true, widthMm: 28 }),
      F('projectCode', '사업코드', { mergeYn: true, widthMm: 20 }),
      F('projectName', '사업명', { mergeYn: true, widthMm: 36 }),
      F('subjectCode', '과목코드', { printDefault: false, mergeYn: true, widthMm: 20 }),
      F('subjectDispCode', '과목코드(표시)', { mergeYn: true, widthMm: 24 }),
      F('subjectName', '과목명', { mergeYn: true, widthMm: 32 }),
      F('fundCode', '재원코드', { printDefault: false, mergeYn: true, widthMm: 18 }),
      F('fundName', '재원명', { mergeYn: true, widthMm: 20 }),
      F('budgetAmt', '예산액(천원)', { type: 'number', sumYn: true, mergeYn: true, widthMm: 24 }),
      F('calcLevel', '산출레벨', { printDefault: true, widthMm: 16 }),
      F('calcDesc', '산출내역', { widthMm: 40 }),
      F('calcBasis', '산출기초', { widthMm: 36 }),
      F('calcAmt', '산출금액(천원)', { type: 'number', sumYn: true, widthMm: 24 }),
      F('masterRemark', '비고', { mergeYn: true, widthMm: 28 }),
      F('detailRemark', '산출비고', { printDefault: false, widthMm: 28 }),
      F('requestUid', '(내부)', { printDefault: false, widthMm: 12 }),
    ],
  },
];

export const REPORT_DEFS = [
  {
    reportId: REPORT_ID.BUDGET_REQ_LINE,
    label: '편성내역',
    datasetId: DATASET_ID.BUDGET_REQ_LINE,
    module: 'BD',
    useYn: 'Y',
  },
];

export const REPORT_DATASET_BY_ID = Object.fromEntries(
  REPORT_DATASETS.map((d) => [d.datasetId, d])
);
export const REPORT_DEF_BY_ID = Object.fromEntries(REPORT_DEFS.map((r) => [r.reportId, r]));

export const REPORT_OPTIONS = REPORT_DEFS.filter((r) => r.useYn === 'Y').map((r) => ({
  value: r.reportId,
  label: r.label,
}));

/** 미리보기용 샘플 — 업무 조회 결과와 무관 */
export const REPORT_SAMPLE_ROWS = {
  [DATASET_ID.BUDGET_REQ_LINE]: [
    {
      requestUid: 'req-demo-1',
      acctUnitCode: 'AU01',
      acctUnitNm: '본청',
      deptCode: 'D100',
      deptName: '기획예산과',
      projectCode: 'P-101',
      projectName: '정보화기반구축',
      subjectCode: '4010101',
      subjectDispCode: '401-01-01',
      subjectName: '일반수용비',
      fundCode: 'F01',
      fundName: '자체',
      budgetAmt: 12000,
      calcLevel: '1',
      calcDesc: '소모품',
      calcBasis: '단가×수량',
      calcAmt: 4000,
      masterRemark: '',
      detailRemark: '',
    },
    {
      requestUid: 'req-demo-1',
      acctUnitCode: 'AU01',
      acctUnitNm: '본청',
      deptCode: 'D100',
      deptName: '기획예산과',
      projectCode: 'P-101',
      projectName: '정보화기반구축',
      subjectCode: '4010101',
      subjectDispCode: '401-01-01',
      subjectName: '일반수용비',
      fundCode: 'F01',
      fundName: '자체',
      budgetAmt: 12000,
      calcLevel: '1',
      calcDesc: '인쇄비',
      calcBasis: '견적',
      calcAmt: 8000,
      masterRemark: '',
      detailRemark: '',
    },
    {
      requestUid: 'req-demo-2',
      acctUnitCode: 'AU02',
      acctUnitNm: '사업소',
      deptCode: 'D210',
      deptName: '시설관리과',
      projectCode: 'P-210',
      projectName: '청사유지',
      subjectCode: '4010201',
      subjectDispCode: '401-02-01',
      subjectName: '공공요금',
      fundCode: 'F01',
      fundName: '자체',
      budgetAmt: 3500,
      calcLevel: '1',
      calcDesc: '전기요금',
      calcBasis: '전년실적',
      calcAmt: 3500,
      masterRemark: '시범',
      detailRemark: '',
    },
  ],
};
